import { useAtomValue, useSetAtom } from 'jotai';

import { DocumentForm } from '@/apps/engagement-letter-database/components/DocumentForm';
import type { DocumentFormData } from '@/apps/engagement-letter-database/components/DocumentForm/types';
import { markEngagementRecordExecutedAtom } from '@/apps/engagement-letter-database/store';
import { useAddEngagementDocumentMutation } from '@/gql/operations';
import { BaseModal } from '@/shared/components/Modal';
import { closeModalAtom, isModalOpenAtom, modalModeAtom } from '@/shared/state/atoms';
import type { RelatedClientRecord } from '@/apps/engagement-letter-database/data/types';

import SelectedClientsList from '../SelectedClientsList';

interface RelatedClientsModalProps {
  selectedClients: RelatedClientRecord['Clients'];
  currentUserMpId: string;
  onDocumentAdded?: () => void;
}

const RelatedClientsModal = ({ selectedClients, currentUserMpId, onDocumentAdded }: RelatedClientsModalProps) => {
  const isOpen = useAtomValue(isModalOpenAtom);
  const mode = useAtomValue(modalModeAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const markRecordExecuted = useSetAtom(markEngagementRecordExecutedAtom);
  const [addDocument] = useAddEngagementDocumentMutation();

  if (mode !== 'add') return null;

  const handleSave = async (formData: DocumentFormData) => {
    const recordIds = [...new Set(selectedClients.flatMap((client) => client.cmap_clm_engagement_record?.id ?? []))];
    await Promise.all(
      recordIds.map(async (recordId) => {
        await addDocument({
          variables: {
            engagement_records_id: recordId,
            name: formData.documentName,
            doc_id: formData.iManageDocNumber,
            created_by: currentUserMpId,
            updated_by: currentUserMpId,
          },
        });
        markRecordExecuted(recordId);
      }),
    );
    onDocumentAdded?.();
    closeModal();
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={() => closeModal()}
      ariaLabelledBy="user-modal-title"
      sx={{ maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}
    >
      <SelectedClientsList selectedClients={selectedClients} />
      <DocumentForm data={null} mode="add" onCancel={() => closeModal()} onSave={handleSave} />
    </BaseModal>
  );
};

export default RelatedClientsModal;
